/**
 * Client-side pacing for streamed agent text.
 *
 * Wire deltas still land in the thread model immediately; this module only
 * decides how many buffered characters to show per animation frame so large
 * coalesced chunks feel like typing instead of phrase dumps.
 */

export interface RevealCursor {
  content: number;
  reasoning: number;
}

export interface RevealTarget {
  content: string;
  reasoning?: string;
  /** True while the wire stream is still open for this message. */
  streaming: boolean;
}

/** Base chars revealed per frame when the backlog is small. */
export const REVEAL_BASE_CHARS = 3;

/** Cap per frame so catch-up stays smooth instead of dumping. */
export const REVEAL_MAX_CHARS = 16;

/**
 * How many characters to reveal this frame given the unread backlog.
 * Speeds up as the queue grows (option B), then clamps at REVEAL_MAX_CHARS.
 */
export function revealStep(backlog: number): number {
  if (backlog <= 0) return 0;
  const scaled = Math.ceil(backlog / 6);
  return Math.min(backlog, Math.max(REVEAL_BASE_CHARS, Math.min(REVEAL_MAX_CHARS, scaled)));
}

/**
 * Advances (or flushes) a reveal cursor toward the current target.
 * When `streaming` is false, jumps to the full lengths immediately.
 */
export function tickReveal(cursor: RevealCursor, target: RevealTarget): RevealCursor {
  const contentLen = target.content.length;
  const reasoningLen = target.reasoning?.length ?? 0;

  if (!target.streaming) {
    return { content: contentLen, reasoning: reasoningLen };
  }

  return {
    content: Math.min(contentLen, cursor.content + revealStep(contentLen - cursor.content)),
    reasoning: Math.min(
      reasoningLen,
      cursor.reasoning + revealStep(reasoningLen - cursor.reasoning),
    ),
  };
}

/** True when the cursor has not yet caught up to the buffered target. */
export function hasRevealBacklog(cursor: RevealCursor, target: RevealTarget): boolean {
  const reasoningLen = target.reasoning?.length ?? 0;
  return cursor.content < target.content.length || cursor.reasoning < reasoningLen;
}

/**
 * Initial cursor for a newly seen agent message: empty while streaming so the
 * first chunk types out; full length for already-finished history rows.
 */
export function initialRevealCursor(target: RevealTarget): RevealCursor {
  if (!target.streaming) {
    return {
      content: target.content.length,
      reasoning: target.reasoning?.length ?? 0,
    };
  }
  return { content: 0, reasoning: 0 };
}

/** Applies a cursor to produce the visible prefix of an agent message. */
export function applyRevealCursor<T extends RevealTarget>(
  target: T,
  cursor: RevealCursor,
): T & { streaming: boolean } {
  const reasoning =
    target.reasoning === undefined
      ? undefined
      : target.reasoning.slice(0, cursor.reasoning);
  const catchingUp = hasRevealBacklog(cursor, target);
  return {
    ...target,
    content: target.content.slice(0, cursor.content),
    reasoning: reasoning || undefined,
    // Keep the caret until the wire ends and the reveal has caught up.
    streaming: target.streaming || catchingUp,
  };
}
